// frontend/src/Components/SyncTo/ModeModal.jsx
//
// Phase 34 — Mode picker + consent gate for SyncTo.
//
// Replaces the Phase 29/30 no-op stub: clicking the SyncTo button now opens
// this modal (via Session.jsx wiring) and FORCES the user to pick Note or
// Template AND tick a consent checkbox before recording can start.
//
// On Start:
//   1. PATCH /api/sync-to/consent (Plan 34-01) — persists syncToConsentAt +
//      syncToConsentUserId on the session doc. Authoritative timestamp is
//      server-generated; first-write-wins on repeat clicks.
//   2. Call onStart(meta) — parent (Session.jsx) forwards into
//      RecordingProvider.openModal(meta) via the SyncToButton flow or
//      direct context call.
//
// Invariants:
//   - Does NOT manage show internally; parent controls open/close.
//   - Does NOT call onStart unless PATCH /consent returned 200.
//   - Renders the logged-in user's name next to the consent checkbox
//     ('You: <firstName> <lastName>') so who is consenting is explicit
//     (PITFALLS #13).
//   - Start button is disabled until:
//       (mode === 'note' OR (mode === 'template' AND templateId)) AND consented.
//   - No PHI logging — zero console.* calls in this file.
//
// Help copy: the modal includes a short tip about keeping the tab
// foregrounded (PITFALLS #17 — Mobile Safari background-tab behaviour is
// documented, not solved).

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:3002';

function getAuthHeader() {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('user') : null;
  return token ? { Authorization: 'Bearer ' + token } : {};
}

function readNameFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return '';
    const decoded = jwtDecode(token);
    const first = decoded?.firstName || '';
    const last = decoded?.lastName || '';
    const combined = (first + ' ' + last).trim();
    return combined || decoded?.email || '';
  } catch {
    return '';
  }
}

function readUserIdFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?._id || decoded?.id || null;
  } catch {
    return null;
  }
}

const CONSENT_LABEL = 'Patient has consented to being recorded';
const FOREGROUND_TIP =
  'Tip — keep this tab foregrounded while recording. Some mobile browsers pause audio capture in background tabs.';

/**
 * ModeModal
 *
 * Props:
 *   show                    — boolean, parent-controlled open state
 *   onClose                 — () => void, cancel/X click
 *   onStart                 — (meta) => void, called AFTER PATCH /consent 200
 *                             meta shape: { mode, templateId?, templatePages?, existingCapturedValues? }
 *   sessionId               — string, REQUIRED (parent MUST have already called ensureSessionExists)
 *   existingSession         — object | null, the session doc (for existingCapturedValues lookup in Template mode)
 *
 * The parent (Session.jsx) passes sessionId + existingSession; ModeModal does
 * not fetch them. This keeps the component UI-pure and leaves session
 * resolution in Session.jsx (matches the 32.1 / 33-04 split of concerns).
 */
export default function ModeModal({ show, onClose, onStart, sessionId, existingSession }) {
  const [mode, setMode] = useState(null);              // 'note' | 'template' | null
  const [templateId, setTemplateId] = useState('');    // selected template _id (string)
  const [templates, setTemplates] = useState([]);      // active templates for user's practice
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [consented, setConsented] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const userName = useMemo(() => readNameFromJwt(), []);

  // Re-initialise state when the modal re-opens.
  useEffect(() => {
    if (!show) return;
    setMode(null);
    setTemplateId('');
    setConsented(false);
    setStarting(false);
    setStartError(null);
  }, [show]);

  // Lazy-load active templates when user picks Template mode.
  useEffect(() => {
    if (!show) return;
    if (mode !== 'template') return;
    if (templates.length > 0) return;          // cache — one fetch per modal open
    const userId = readUserIdFromJwt();
    if (!userId) {
      setTemplatesError('Could not identify user');
      return;
    }
    let cancelled = false;
    setLoadingTemplates(true);
    setTemplatesError(null);
    axios
      .get(`${URL}/templates/${userId}`, { headers: { ...getAuthHeader() } })
      .then((resp) => {
        if (cancelled) return;
        const all = Array.isArray(resp.data) ? resp.data : [];
        // Filter CLIENT-SIDE to templateStatus === 'Active' (backend
        // templateRoutes.js ALLOWED_STATUS is PascalCase: "Active" | "Inactive"
        // | "Draft" — the 'active' lowercase variant never matches stored docs).
        const active = all.filter((t) => t && t.templateStatus === 'Active');
        setTemplates(active);
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplatesError(err?.response?.data?.message || err?.message || 'Failed to load templates');
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, [show, mode, templates.length]);

  const startDisabled =
    starting ||
    !sessionId ||
    !mode ||
    (mode === 'template' && !templateId) ||
    !consented;

  const onStartClick = async () => {
    if (startDisabled) return;
    setStarting(true);
    setStartError(null);
    // Step 1 — PATCH /api/sync-to/consent (Plan 34-01).
    try {
      await axios.patch(
        `${URL}/api/sync-to/consent`,
        { sessionId },
        { headers: { 'Content-Type': 'application/json', ...getAuthHeader() } },
      );
    } catch (err) {
      setStarting(false);
      setStartError(err?.response?.data?.error || err?.message || 'Consent write failed');
      return;
    }
    // Step 2 — build meta and hand off to parent (which calls RecordingProvider.openModal).
    let meta;
    if (mode === 'template') {
      const chosen = templates.find((t) => String(t._id) === String(templateId));
      meta = {
        mode: 'template',
        templateId,
        templatePages: Array.isArray(chosen?.pages) ? chosen.pages : [],
        existingCapturedValues: (existingSession && existingSession.capturedValues) || {},
      };
    } else {
      meta = { mode: 'note' };
    }
    setStarting(false);
    if (typeof onStart === 'function') onStart(meta);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="responsive-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Start SyncTo recording</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {startError && (
          <Alert variant="danger" className="mb-3">
            {startError}
          </Alert>
        )}

        <div className="mb-3">
          <div className="small text-muted mb-2">Choose what to capture:</div>
          <div className="d-flex gap-2">
            <Button
              variant={mode === 'note' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('note')}
              disabled={starting}
              aria-pressed={mode === 'note'}
            >
              <i className="fa-light fa-notes-medical me-2" />
              Note
            </Button>
            <Button
              variant={mode === 'template' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('template')}
              disabled={starting}
              aria-pressed={mode === 'template'}
            >
              <i className="fa-light fa-file-lines me-2" />
              Template
            </Button>
          </div>
        </div>

        {mode === 'template' && (
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted mb-1">Active template</Form.Label>
            {loadingTemplates ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span className="small text-muted">Loading active templates…</span>
              </div>
            ) : templatesError ? (
              <Alert variant="warning" className="mb-0 py-2 small">
                {templatesError}
              </Alert>
            ) : templates.length === 0 ? (
              <Alert variant="info" className="mb-0 py-2 small">
                No active templates for this practice. Publish a template first or choose Note mode.
              </Alert>
            ) : (
              <Form.Select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={starting}
                aria-label="Active template selector"
              >
                <option value="">Select a template…</option>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.templateTitle || '(untitled)'}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Check
            type="checkbox"
            id="syncto-consent-checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            disabled={starting}
            label={
              <span>
                <strong>{CONSENT_LABEL}</strong>
                {userName && (
                  <span className="text-muted ms-2">(You: {userName})</span>
                )}
              </span>
            }
          />
        </Form.Group>

        <div className="small text-muted">{FOREGROUND_TIP}</div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button variant="outline-secondary" onClick={onClose} disabled={starting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onStartClick} disabled={startDisabled}>
            {starting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Starting…
              </>
            ) : (
              'Start recording'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
